import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import { getCompanyEvaluationFormsRequest } from "../../../../store/CompanyPortal/Evaluations/actions";
import { APPLICANT_EVALUATION_STATUSES } from "./constants";
import { DEFAULT_APPLICANT_SORT_MODEL, sortApplicantRows } from "./applicantEvaluationsSorting";

const DEFAULT_PAGE_SIZE = 10;

const uniqueOptions = (rows, idKey, nameKey) => {
  const seen = new Map();
  rows.forEach((r) => {
    if (r[idKey] != null && !seen.has(r[idKey])) {
      seen.set(r[idKey], { id: r[idKey], name: r[nameKey] });
    }
  });
  return [...seen.values()];
};

// Forms for one round are fetched in full (page=-1); search / filters and
// pagination are applied on the client.
const useApplicantEvaluations = (roundId, portalVariant = "company-portal") => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const forms = useSelector((state) => state.companyEvaluations.forms.list);
  const t = messages[lang].events.viewEvents.evaluationsTab.applicantsView;

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ zone: null, role: null, status: null });
  const [sortModel, setSortModel] = useState(DEFAULT_APPLICANT_SORT_MODEL);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // isFetching guards against the stale-Redux-data flash: forms.list is NOT
  // cleared on request (see reducer comment), so during the async gap between
  // dispatch and response the list may belong to a different round, producing
  // 0 filtered rows and flashing <UIEmptyState> before real data arrives.
  const [isFetching, setIsFetching] = useState(!!roundId);
  const prevFormsRef = useRef(forms);

  useEffect(() => {
    if (roundId) {
      setIsFetching(true);
      dispatch(
        getCompanyEvaluationFormsRequest({
          evaluation_id: roundId,
          page: -1,
          portal: portalVariant,
        }),
      );
    }
  }, [dispatch, roundId, portalVariant]);

  // Clear isFetching once the forms reference changes — that is the signal
  // that the saga response has landed and Redux now holds current-round data.
  useEffect(() => {
    if (forms !== prevFormsRef.current) {
      prevFormsRef.current = forms;
      setIsFetching(false);
    }
  }, [forms]);

  const zoneOptions = useMemo(
    () => uniqueOptions(forms, "zoneId", "zoneName"),
    [forms],
  );
  const roleOptions = useMemo(
    () => uniqueOptions(forms, "roleId", "role"),
    [forms],
  );
  const statusOptions = useMemo(
    () => [
      { id: APPLICANT_EVALUATION_STATUSES.pending, name: t.table.statuses.pending },
      { id: APPLICANT_EVALUATION_STATUSES.done, name: t.table.statuses.done },
    ],
    [t],
  );

  const filteredRows = useMemo(() => {
    let rows = forms;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.applicantName.toLowerCase().includes(q) ||
          r.role.toLowerCase().includes(q),
      );
    }

    if (filters.zone) {
      rows = rows.filter((r) => r.zoneId === filters.zone.id);
    }
    if (filters.role) {
      rows = rows.filter((r) => r.roleId === filters.role.id);
    }
    if (filters.status) {
      rows = rows.filter((r) => r.status === filters.status.id);
    }

    return sortApplicantRows(rows, sortModel);
  }, [forms, search, filters, sortModel]);

  const total = filteredRows.length;
  const pages = Math.ceil(total / pageSize) || 1;
  const safePageIndex = Math.min(page, pages);
  const rangeFrom = total === 0 ? 0 : (safePageIndex - 1) * pageSize + 1;
  const rangeTo = Math.min(safePageIndex * pageSize, total);
  const paginatedRows = filteredRows.slice(
    (safePageIndex - 1) * pageSize,
    safePageIndex * pageSize,
  );

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const handleSortModelChange = (nextSortModel) => {
    setSortModel(nextSortModel.length ? nextSortModel : DEFAULT_APPLICANT_SORT_MODEL);
    setPage(1);
  };

  return {
    search,
    filters,
    sortModel,
    page,
    pageSize,
    rows: paginatedRows,
    total,
    pages,
    rangeFrom,
    rangeTo,
    zoneOptions,
    roleOptions,
    statusOptions,
    isFetching,
    handleSearchChange,
    handleFilterChange,
    handleSortModelChange,
    handlePageChange: setPage,
    handlePageSizeChange,
  };
};

export default useApplicantEvaluations;
